import { useState, useEffect } from 'react';

interface Tip {
  id: string;
  title: string;
  content: React.ReactNode;
}

const TIPS: Tip[] = [
  {
    id: 'calendar-feed',
    title: 'Abonnér på klan-kalenderen',
    content: (
      <div>
        <p style={{ marginBottom: '0.75rem' }}>
          Du kan få alle Gribklan-begivenheder direkte ind i din egen kalender — automatisk og altid opdateret!
        </p>
        <p style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Sådan gør du:</p>
        <ol style={{ paddingLeft: '1.4rem', lineHeight: 1.9 }}>
          <li>Gå til <strong>Begivenheder</strong> i menuen.</li>
          <li>Tryk på knappen <strong>"Kopiér kalender-URL"</strong> øverst på siden.</li>
          <li>
            <strong>iPhone/iPad (Apple Calendar):</strong> Åbn Indstillinger → Kalender → Konti → Tilføj konto → Andet → Tilføj abonnementskalender. Indsæt URL'en.
          </li>
          <li>
            <strong>Android (Google Calendar):</strong> Åbn <em>calendar.google.com</em> i en browser → Klik "+" ved "Andre kalendere" → Fra URL. Indsæt URL'en.
          </li>
          <li>
            <strong>Outlook:</strong> Kalender → Tilføj kalender → Abonnér via internettet. Indsæt URL'en.
          </li>
        </ol>
        <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9em' }}>
          Begivenheder du har frameldt dig vises ikke i dit feed. Kalenderen indeholder automatisk en notifikation 2 timer før hvert arrangement.
        </p>
      </div>
    ),
  },
  {
    id: 'profile-picture',
    title: 'Tilføj et profilbillede',
    content: (
      <div>
        <p style={{ marginBottom: '0.75rem' }}>
          Giv dig selv et ansigt i klanen! Et profilbillede gør det nemmere for andre at kende dig.
        </p>
        <p style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Sådan gør du:</p>
        <ol style={{ paddingLeft: '1.4rem', lineHeight: 1.9 }}>
          <li>Tryk på dit navn eller avatar øverst i menuen.</li>
          <li>Vælg <strong>"Rediger profil"</strong>.</li>
          <li>Tryk på kamerasymbolet ved siden af din nuværende avatar.</li>
          <li>Vælg et billede fra din telefon eller computer.</li>
          <li>Tryk <strong>"Gem"</strong> — det er det hele!</li>
        </ol>
        <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9em' }}>
          Du kan også opdatere dit navn og en kort beskrivelse om dig selv på profilsiden.
        </p>
      </div>
    ),
  },
  {
    id: 'chat',
    title: 'Chat og gruppechat',
    content: (
      <div>
        <p style={{ marginBottom: '0.75rem' }}>
          Vidste du, at du kan skrive direkte til andre klanmedlemmer — eller oprette en gruppechat med flere på én gang?
        </p>
        <p style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Sådan starter du en chat:</p>
        <ol style={{ paddingLeft: '1.4rem', lineHeight: 1.9 }}>
          <li>Gå til <strong>Beskeder</strong> i menuen (taleboble-ikonet).</li>
          <li>Tryk på <strong>"Ny besked"</strong>.</li>
          <li>Søg efter og vælg det/de medlemmer, du vil skrive med.</li>
          <li>Vælg ét medlem for en direkte besked, eller vælg <strong>flere</strong> for at starte en gruppechat.</li>
          <li>Skriv din besked og tryk send!</li>
        </ol>
        <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9em' }}>
          Du kan selv navngive en gruppechat, så det er nemt at finde den igen.
        </p>
      </div>
    ),
  },
  {
    id: 'notification-settings',
    title: 'Tilpas dine notifikationer',
    content: (
      <div>
        <p style={{ marginBottom: '0.75rem' }}>
          Du bestemmer selv, hvilke notifikationer du modtager — og om de skal komme som notifikation på skærmen, push-besked eller e-mail.
        </p>
        <p style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Sådan finder du indstillingerne:</p>
        <ol style={{ paddingLeft: '1.4rem', lineHeight: 1.9 }}>
          <li>Gå til <strong>Notifikationer</strong> (klokke-ikonet i menuen).</li>
          <li>Tryk på <strong>tandhjulet</strong> øverst til højre.</li>
          <li>Slå de ønskede notifikationstyper til eller fra — fx likes, kommentarer, beskeder og begivenheder.</li>
        </ol>
        <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9em' }}>
          Du kan fx vælge kun at modtage e-mails om begivenheder, men stadig få push-beskeder om nye beskeder.
        </p>
      </div>
    ),
  },
];

const DISMISSED_KEY = 'gribklan_dismissed_tips';
const CURRENT_TIP_KEY = 'gribklan_current_tip_index';

function getDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
}

function getNextTip(dismissed: Set<string>): Tip | null {
  // Try to resume the last shown tip index
  const savedIndex = parseInt(localStorage.getItem(CURRENT_TIP_KEY) ?? '0', 10);
  // Find tips that haven't been dismissed, starting from savedIndex
  for (let i = 0; i < TIPS.length; i++) {
    const tip = TIPS[(savedIndex + i) % TIPS.length];
    if (!dismissed.has(tip.id)) {
      localStorage.setItem(CURRENT_TIP_KEY, String((savedIndex + i) % TIPS.length));
      return tip;
    }
  }
  return null;
}

export function TipOfTheDay() {
  const [dismissed, setDismissed] = useState<Set<string>>(getDismissed);
  const [tip, setTip] = useState<Tip | null>(() => getNextTip(getDismissed()));
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setTip(getNextTip(dismissed));
  }, [dismissed]);

  if (!tip) return null;

  function handleDismiss() {
    const next = new Set(dismissed);
    next.add(tip!.id);
    saveDismissed(next);
    setDismissed(next);
    setExpanded(false);
  }

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #d1fae5 0%, #e0f2fe 100%)',
        border: '1px solid #6ee7b7',
        borderRadius: '12px',
        marginBottom: '1.25rem',
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        aria-expanded={expanded}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.2rem' }}>💡</span>
          <span style={{ fontWeight: 700, color: '#065f46', fontSize: '0.95rem' }}>
            Dagens tip:&nbsp;
          </span>
          <span style={{ color: '#047857', fontSize: '0.95rem' }}>{tip.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: '#059669', fontSize: '0.85rem', fontWeight: 500 }}>
            {expanded ? 'Skjul ▲' : 'Vis ▼'}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            title="Vis ikke dette tip igen"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '1.1rem',
              lineHeight: 1,
              padding: '0 0.25rem',
            }}
            aria-label="Luk tip"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div
          style={{
            borderTop: '1px solid #a7f3d0',
            padding: '1rem 1.1rem 1.1rem',
            color: '#1f2937',
            fontSize: '0.95rem',
            lineHeight: 1.7,
          }}
        >
          {tip.content}
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              style={{ fontSize: '0.85rem', padding: '0.4em 1.1em' }}
              onClick={handleDismiss}
            >
              Forstået — vis ikke igen
            </button>
            <button
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: '0.85rem',
                cursor: 'pointer',
                padding: '0.4em 0.5em',
              }}
              onClick={() => setExpanded(false)}
            >
              Luk
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
